import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { cartAPI, checkoutAPI, profileAPI } from '../lib/api';
import { getGuestSessionId } from '../lib/pendingCart';
import { getCartItemLicenseLabel } from '../lib/productLicenses';
import PersonalDataConsent from '../components/PersonalDataConsent';
import MarketingConsent from '../components/MarketingConsent';
import './CheckoutPage.css';

function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [orderResult, setOrderResult] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [consent, setConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  const isAuthenticated = !!localStorage.getItem('token');
  const sessionId = getGuestSessionId();

  const [contact, setContact] = useState({
    contact_name: '',
    email: '',
    phone: '',
    company_name: '',
    comment: ''
  });

  const [legal, setLegal] = useState({
    organization_name: '',
    inn: '',
    kpp: '',
    legal_address: '',
    settlement_account: '',
    bik: '',
    bank_name: '',
    correspondent_account: ''
  });

  // Сводка по корзине (обновляется при загрузке; отображение идёт по позициям items)
  const [, setSummary] = useState({
    monthly_total: 0,
    one_time_total: 0,
    grand_total: 0
  });

  useEffect(() => {
    const restoreStep = location.state?.restoreStep;
    if (restoreStep && Number.isInteger(restoreStep)) {
      // UI только для шагов 1 (форма) и 3 (успех); шаг 2 не используется
      setStep(restoreStep === 3 ? 3 : 1);
    }
  }, [location.state]);

  useEffect(() => {
    const loadCart = async () => {
      try {
        setLoading(true);
        if (isAuthenticated) {
          const cartItems = await cartAPI.getCart();
          setItems(cartItems);
          const sum = await cartAPI.getSummary();
          setSummary({
            monthly_total: sum.monthly_total || 0,
            one_time_total: sum.one_time_total || 0,
            grand_total: sum.grand_total || 0
          });
        } else {
          const cart = await cartAPI.getGuestCart(sessionId);
          setItems(cart.items || []);
          let monthly = 0;
          let onetime = 0;
          cart.items?.forEach(item => {
            const calc = item.calculator_data || {};
            monthly += parseFloat(calc.monthlyPrice || calc.monthly_price || 0);
            onetime += parseFloat(calc.oneTimePrice || calc.one_time_price || 0);
          });
          setSummary({
            monthly_total: monthly,
            one_time_total: onetime,
            grand_total: monthly + onetime
          });
        }
      } catch (err) {
        console.error('Error loading cart:', err);
        setError('Не удалось загрузить корзину');
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, [isAuthenticated, sessionId]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const loadProfile = async () => {
      try {
        const data = await profileAPI.getProfile();
        if (!data) return;

        setProfileLoaded(true);
        setContact(prev => ({
          ...prev,
          contact_name: prev.contact_name || data.contact_name || data.name || '',
          email: prev.email || data.email || '',
          phone: prev.phone || data.phone || '',
          company_name: prev.company_name || data.company_name || data.organization_name || ''
        }));

        setLegal(prev => ({
          ...prev,
          organization_name: prev.organization_name || data.organization_name || data.company_name || '',
          inn: prev.inn || data.inn || '',
          kpp: prev.kpp || data.kpp || '',
          legal_address: prev.legal_address || data.legal_address || '',
          settlement_account: prev.settlement_account || data.settlement_account || '',
          bik: prev.bik || data.bik || '',
          bank_name: prev.bank_name || data.bank_name || '',
          correspondent_account: prev.correspondent_account || data.correspondent_account || ''
        }));
      } catch (err) {
        console.error('Error loading profile:', err);
      }
    };

    loadProfile();
  }, [isAuthenticated]);

  const formatPrice = (price) => (parseFloat(price) || 0).toLocaleString('ru-RU');

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContact(prev => ({ ...prev, [name]: value }));
  };

  const handleLegalChange = (e) => {
    const { name, value } = e.target;
    setLegal(prev => ({ ...prev, [name]: value }));
  };

  const reloadCart = async () => {
    try {
      if (isAuthenticated) {
        const cartItems = await cartAPI.getCart();
        setItems(cartItems);
        const sum = await cartAPI.getSummary();
        setSummary({
          monthly_total: sum.monthly_total || 0,
          one_time_total: sum.one_time_total || 0,
          grand_total: sum.grand_total || 0
        });
      } else {
        const cart = await cartAPI.getGuestCart(sessionId);
        setItems(cart.items || []);
        let monthly = 0;
        let onetime = 0;
        cart.items?.forEach(item => {
          const calc = item.calculator_data || {};
          monthly += parseFloat(calc.monthlyPrice || calc.monthly_price || 0);
          onetime += parseFloat(calc.oneTimePrice || calc.one_time_price || 0);
        });
        setSummary({
          monthly_total: monthly,
          one_time_total: onetime,
          grand_total: monthly + onetime
        });
      }
    } catch (err) {
      console.error('Error reloading cart:', err);
    }
  };

  const handleQuantityChange = async (itemId, newQty) => {
    if (newQty < 1) return;
    try {
      if (isAuthenticated) {
        await cartAPI.updateQuantity(itemId, newQty);
      } else {
        await cartAPI.updateGuestCartItem(itemId, sessionId, newQty);
      }
      await reloadCart();
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const handleRemove = async (itemId) => {
    try {
      if (isAuthenticated) {
        await cartAPI.removeFromCart(itemId);
      } else {
        await cartAPI.removeFromGuestCart(itemId, sessionId);
      }
      await reloadCart();
    } catch (err) {
      console.error('Error removing item:', err);
    }
  };

  const handleSubmit = async () => {
    if (!consent) {
      setError('Необходимо согласие на обработку персональных данных');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      const result = await checkoutAPI.createOrder({
        cart_item_ids: items.map(item => item.id),
        session_id: isAuthenticated ? null : sessionId,
        contact,
        legal,
        payment_notes: ''
      }, consent, marketingConsent);
      setOrderResult(result);
      setStep(3);
    } catch (err) {
      console.error('Error creating order:', err);
      setError(err.message || 'Ошибка при оформлении заказа');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="checkout-page"><Header /><div className="checkout-loading"><div className="spinner" /><p>Загрузка...</p></div></div>;
  }

  if (items.length === 0 && !orderResult) {
    return <div className="checkout-page"><Header /><div className="checkout-empty"><p>Корзина пуста</p><Link to="/solutions">В каталог</Link></div></div>;
  }

  return (
    <div className="checkout-page">
      <Header />
      <div className="checkout-container">
        {error && <div className="checkout-error">{error}</div>}

        {step === 1 && (
          <div className="step-content">
            <h2>Состав заказа и данные</h2>
            <div className="order-items">
              {items.map(item => {
                const calc = item.calculator_data || {};
                const monthly = calc.monthlyPrice || calc.monthly_price || 0;
                const onetime = calc.oneTimePrice || calc.one_time_price || 0;
                const licenseLabel = getCartItemLicenseLabel(calc);
                return (
                  <div key={item.id} className="order-item">
                    <div className="item-main">
                      <div className="item-name">{item.product?.name || item.product_name}</div>
                      {licenseLabel && (
                        <div className="item-detail">{licenseLabel}</div>
                      )}
                      {calc.selectedServices?.length > 0 && (
                        <div className="item-services">
                          <div className="services-label">Включено:</div>
                          {calc.selectedServices.map((svc, i) => (
                            <div key={i} className="service-item">• {svc.name}: {svc.price > 0 ? formatPrice(svc.price) + ' ₽' : (svc.priceDisplay || 'по запросу')}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="item-actions">
                      <div className="quantity-control">
                        <button onClick={() => handleQuantityChange(item.id, (item.quantity || 1) - 1)} disabled={(item.quantity || 1) <= 1}>−</button>
                        <span>{item.quantity || 1}</span>
                        <button onClick={() => handleQuantityChange(item.id, (item.quantity || 1) + 1)}>+</button>
                      </div>
                      <div className="item-price">
                        {(monthly > 0 || onetime > 0) ? <><span>{monthly > 0 && `${formatPrice(monthly)} ₽/мес`}</span><span>{onetime > 0 && `${formatPrice(onetime)} ₽`}</span></> : <span className="price-on-request">по запросу</span>}
                      </div>
                      <button className="remove-btn" onClick={() => handleRemove(item.id)}>✕</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="checkout-auth-box" style={{ marginTop: 24 }}>
              {!isAuthenticated ? (
                <>
                  <p>Войдите, чтобы автозаполнить поля профиля.</p>
                  <Button type="button" onClick={() => navigate('/login', { state: { from: { pathname: '/checkout', restoreStep: 1 } } })}>Войти</Button>
                </>
              ) : (
                <p>{profileLoaded ? 'Данные профиля подгружены автоматически.' : 'Загрузка данных профиля...'}</p>
              )}
            </div>

            <div className="form-grid" style={{ marginTop: 24 }}>
              <div className="form-group"><label>ФИО *</label><input type="text" name="contact_name" value={contact.contact_name} onChange={handleContactChange} /></div>
              <div className="form-group"><label>Email *</label><input type="email" name="email" value={contact.email} onChange={handleContactChange} /></div>
              <div className="form-group"><label>Телефон *</label><input type="tel" name="phone" value={contact.phone} onChange={handleContactChange} /></div>
              <div className="form-group"><label>Компания</label><input type="text" name="company_name" value={contact.company_name} onChange={handleContactChange} /></div>
              <div className="form-group full-width"><label>Комментарий</label><textarea name="comment" value={contact.comment} onChange={handleContactChange} rows={3} /></div>
              <div className="form-group"><label>Название организации *</label><input type="text" name="organization_name" value={legal.organization_name} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>ИНН *</label><input type="text" name="inn" value={legal.inn} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>КПП</label><input type="text" name="kpp" value={legal.kpp} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>Юридический адрес</label><input type="text" name="legal_address" value={legal.legal_address} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>Расчётный счёт *</label><input type="text" name="settlement_account" value={legal.settlement_account} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>БИК *</label><input type="text" name="bik" value={legal.bik} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>Банк *</label><input type="text" name="bank_name" value={legal.bank_name} onChange={handleLegalChange} /></div>
              <div className="form-group"><label>Корр. счёт</label><input type="text" name="correspondent_account" value={legal.correspondent_account} onChange={handleLegalChange} /></div>
            </div>

            <div style={{ marginTop: 16 }}>
              <PersonalDataConsent
                id="checkout-pd-consent"
                checked={consent}
                onCheckedChange={(v) => setConsent(Boolean(v))}
              />
              <MarketingConsent
                id="checkout-marketing-consent"
                className="mt-3"
                checked={marketingConsent}
                onCheckedChange={(v) => setMarketingConsent(Boolean(v))}
              />
            </div>

            <div className="checkout-auth-return" style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <Button variant="outline" onClick={() => navigate('/solutions')}>Вернуться в каталог</Button>
              <Button onClick={handleSubmit} disabled={submitting}>{submitting ? 'Отправка...' : 'Оформить заказ'}</Button>
            </div>
          </div>
        )}

        {step === 3 && orderResult && (
          <div className="checkout-success">
            <div className="success-icon">✓</div>
            <h2>Заказ принят</h2>
            <p className="order-number">№ {orderResult.order_number}</p>
            <div className="success-details">
              <p>На email <strong>{contact.email}</strong> отправлено подтверждение.</p>
              <p>Мы свяжемся с вами для выставления счёта.</p>
            </div>
            <Link to="/" className="success-home-btn">На главную</Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default CheckoutPage;
